/**
 * Сессия секретного чата: X3DH при первом сообщении, дальше Double Ratchet.
 * Здесь связаны воедино три нижних слоя (`x3dh`, `ratchet`, `envelope`) —
 * ровно то, что нужно движку клиента и мок-собеседнику, и ничего про сеть
 * или IndexedDB: сессия — значение, которое сохраняет вызывающий код.
 */
import { concat, equal } from './bytes'
import { decodeEnvelope, encodeEnvelope, encodeHeader } from './envelope'
import { decodePayload, encodePayload, type SecretPayload } from './payload'
import { importDhPrivate } from './primitives'
import {
  initAsInitiator,
  initAsResponder,
  ratchetDecrypt,
  ratchetEncrypt,
  type RatchetState,
} from './ratchet'
import { initiate, respond, type InitialHeader, type LocalIdentity, type PrekeyBundle } from './x3dh'

export interface SecretSession {
  ratchet: RatchetState
  /** IK_A ‖ IK_B из X3DH — AAD каждого сообщения. */
  associatedData: Uint8Array
  /** Пока собеседник не ответил, инициатор повторяет X3DH-заголовок в каждом сообщении. */
  initial: InitialHeader | null
  /** Ключи устройства собеседника, зафиксированные при установлении сессии. */
  remoteSigningKey: Uint8Array
  remoteIdentityKey: Uint8Array
  /** Получено ли хоть одно сообщение собеседника (после этого X3DH-заголовок не нужен). */
  established: boolean
}

/** Инициатор: по набору ключей собеседника строит сессию, готовую шифровать. */
export async function openSession(me: LocalIdentity, bundle: PrekeyBundle): Promise<SecretSession> {
  const agreed = await initiate(me, bundle)
  return {
    ratchet: await initAsInitiator(agreed.sharedSecret, agreed.remoteRatchetKey),
    associatedData: agreed.associatedData,
    initial: agreed.header,
    remoteSigningKey: bundle.signingKey,
    remoteIdentityKey: bundle.identityKey,
    established: false,
  }
}

/** Приватные prekeys отвечающей стороны, которые нужны для первого сообщения. */
export interface ResponderPrekeys {
  signedPrekeyPrivate: Uint8Array
  signedPrekeyPublic: Uint8Array
  oneTimePrekeyPrivate: Uint8Array | null
}

/**
 * Отвечающая сторона: первое сообщение несёт X3DH-заголовок, из него
 * выводится общий секрет, и тут же расшифровывается само сообщение.
 */
export async function acceptSession(
  me: Pick<LocalIdentity, 'identity'>,
  prekeys: ResponderPrekeys,
  envelopeText: string,
): Promise<{ session: SecretSession; payload: SecretPayload }> {
  const envelope = decodeEnvelope(envelopeText)
  if (!envelope.initial) throw new Error('secret_session_missing')
  const signedPrekey = await importDhPrivate(prekeys.signedPrekeyPrivate)
  const oneTime = prekeys.oneTimePrekeyPrivate ? await importDhPrivate(prekeys.oneTimePrekeyPrivate) : null
  const agreed = await respond(me, signedPrekey, oneTime, envelope.initial)
  const session: SecretSession = {
    ratchet: initAsResponder(agreed.sharedSecret, prekeys.signedPrekeyPrivate, prekeys.signedPrekeyPublic),
    associatedData: agreed.associatedData,
    initial: null,
    remoteSigningKey: envelope.initial.signingKey,
    remoteIdentityKey: envelope.initial.identityKey,
    established: true,
  }
  return decryptWith(session, envelope)
}

export async function encryptMessage(
  session: SecretSession,
  payload: SecretPayload,
): Promise<{ session: SecretSession; envelope: string }> {
  const initial = session.established ? null : session.initial
  const result = await ratchetEncrypt(session.ratchet, encodePayload(payload), (header) =>
    concat(session.associatedData, encodeHeader(initial, header)),
  )
  return {
    session: { ...session, ratchet: result.state },
    envelope: encodeEnvelope({ initial, header: result.header, ciphertext: result.ciphertext }),
  }
}

async function decryptWith(
  session: SecretSession,
  envelope: ReturnType<typeof decodeEnvelope>,
): Promise<{ session: SecretSession; payload: SecretPayload }> {
  const result = await ratchetDecrypt(
    session.ratchet,
    envelope.header,
    envelope.ciphertext,
    concat(session.associatedData, envelope.headerBytes),
  )
  return {
    session: { ...session, ratchet: result.state, established: true, initial: null },
    payload: decodePayload(result.plaintext),
  }
}

/**
 * Расшифровка в установленной сессии. Повторный X3DH-заголовок инициатора
 * допустим, но обязан нести те же ключи: другой identity — подмена собеседника.
 */
export async function decryptMessage(
  session: SecretSession,
  envelopeText: string,
): Promise<{ session: SecretSession; payload: SecretPayload }> {
  const envelope = decodeEnvelope(envelopeText)
  if (envelope.initial) {
    if (
      !equal(envelope.initial.identityKey, session.remoteIdentityKey) ||
      !equal(envelope.initial.signingKey, session.remoteSigningKey)
    ) {
      throw new Error('secret_key_changed')
    }
  }
  return decryptWith(session, envelope)
}
